import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled, { type CSSProperties } from 'styled-components';

import { Check } from '@sorare/core/src/atoms/icons/Check';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS } from '@sorare/core/src/atoms/typography';

import { taskThresholdscColors } from '#src/components/FixtureCompetitionTrckPage/TrckGroupPVECompetitionPrizePool/utils.ts';

import { ScoreIndicator } from './ScoreIndicator/index.tsx';
import type { ThresholdsBar_thresholdStreakTaskThreshold } from './__generated__/index.graphql.ts';

const Thresholds = styled(Horizontal).attrs({ gap: 0 })`
  position: relative;
  isolation: isolate;

  & > * {
    flex: 1;
    height: 40px;
  }
  & > :first-child {
    border-radius: var(--unit) 0 0 var(--unit);
    padding-left: 6px;
  }
  & > :last-child {
    border-radius: 0 var(--unit) var(--unit) 0;
  }
`;
const Treshold = styled(Horizontal)`
  position: relative;
  padding: var(--half-unit);
  background-color: var(--color);
  align-items: flex-end;

  &.disabled {
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: rgba(var(--c-rgb-black), 0.8);
    }
  }
`;
const TresholdStatus = styled(Horizontal).attrs({ center: true })`
  flex: 1;
`;
const ScoreIndicatorWrapper = styled.div`
  position: absolute;
  z-index: 1;
  top: -30%;
  left: var(--score-percentage);
`;

type Props = {
  currentThresholdIndex: number;
  thresholds: ThresholdsBar_thresholdStreakTaskThreshold[];
  currentBestScore?: number;
};
export const ThresholdsBar = ({
  currentThresholdIndex,
  thresholds,
  currentBestScore,
}: Props) => {
  const currentThresholdScore = thresholds.at(currentThresholdIndex)?.score;
  const nextThresholdScore = thresholds.at(currentThresholdIndex + 1)?.score;

  const barLeft = (() => {
    if (
      currentBestScore === undefined ||
      !currentThresholdScore ||
      !nextThresholdScore
    ) {
      return null;
    }

    const base = (100 / thresholds.length) * currentThresholdIndex;

    return (
      base +
      ((currentBestScore - currentThresholdScore) /
        (nextThresholdScore - currentThresholdScore)) *
        100
    );
  })();

  return (
    <Vertical gap={2}>
      <Thresholds>
        {thresholds.map(({ score }, i) => {
          return (
            <Treshold
              key={score}
              className={classNames({ disabled: currentThresholdIndex !== i })}
              style={
                {
                  '--color':
                    currentThresholdIndex > i
                      ? 'var(--c-score-high)'
                      : taskThresholdscColors[i].color,
                } as CSSProperties
              }
            >
              <BrandXS color="var(--c-black)">{score}</BrandXS>
            </Treshold>
          );
        })}
        {currentBestScore !== undefined && (
          <ScoreIndicatorWrapper
            style={{ '--score-percentage': `${barLeft}%` } as CSSProperties}
          >
            <ScoreIndicator />
          </ScoreIndicatorWrapper>
        )}
      </Thresholds>
      <Horizontal gap={0}>
        {thresholds.map(({ score }, i) => {
          return (
            <TresholdStatus key={score}>
              <BrandXS
                color={
                  currentThresholdIndex === i
                    ? taskThresholdscColors[i].color
                    : 'var(--c-nd-400)'
                }
              >
                {currentThresholdIndex > i ? (
                  <Check color="var(--c-score-high)" />
                ) : (
                  i + 1
                )}
              </BrandXS>
            </TresholdStatus>
          );
        })}
      </Horizontal>
    </Vertical>
  );
};

ThresholdsBar.fragments = {
  thresholdStreakTaskThreshold: gql`
    fragment ThresholdsBar_thresholdStreakTaskThreshold on ThresholdStreakTaskThreshold {
      score
    }
  ` as TypedDocumentNode<ThresholdsBar_thresholdStreakTaskThreshold>,
};
